import React from 'react';
import type { AsyncProps } from 'react-select/async';
import AsyncSelect from 'react-select/async';
import type { SelectInstance } from 'react-select';
import type { StylesConfig, ClassNamesConfig } from 'react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Cross1Icon } from '@radix-ui/react-icons';

import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/* Reuse your existing cva() variant definition so that
   the Async select has matching styles */
const multiselectVariants = cva(
  cn(
    'flex w-full text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground relative',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-within:border-primary-foreground focus-within:ring-1 focus-within:ring-primary',
    'min-h-[48px] w-full rounded-lg border border-input bg-background text-sm transition-colors',
    'border-box items-center',
  ),
  {
    variants: {
      variant: {
        outlined: ['rounded-md bg-background border', 'ea-input__outlined'],
        filled: ['rounded-md bg-input border-none', 'ea-input__filled'],
      },

      rounded: {
        default: ['rounded-lg', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

/** Extend AsyncProps so we can add styling props and match your existing style API. */
export interface MultiSelectAsyncProps
  extends
    VariantProps<typeof multiselectVariants>,
    Omit<AsyncProps<any, boolean, any>, 'classNames'> {
  /** Optionally pass in a custom placeholder text. */
  placeholder?: string;
  /** Node or icon to show before the input, e.g. a search icon. */
  before?: React.ReactNode;
  /** react-select styles override. */
  stylesConfig?: StylesConfig;
  /** react-select classNames override. */
  classNamesConfig?: ClassNamesConfig;
  /** Optional custom loading state outside of standard react-select's. */
  isLoading?: boolean;
}

export const MultiSelectAsync = React.forwardRef<
  SelectInstance<any, boolean>,
  MultiSelectAsyncProps
>(
  (
    {
      placeholder,
      stylesConfig,
      classNamesConfig,
      isLoading,
      before,
      instanceId,
      // All other props from react-select/async
      ...props
    },
    ref,
  ) => {
    // Without an explicit instanceId react-select numbers its DOM ids from a
    // module-global counter, which keeps climbing across requests on the server
    // and restarts at 1 in the browser — the ids then disagree and hydration
    // fails. useId is stable across both.
    const generatedInstanceId = React.useId();

    return (
      <AsyncSelect
        ref={ref}
        instanceId={instanceId ?? generatedInstanceId}
        unstyled
        placeholder={placeholder}
        isLoading={!!isLoading}
        loadingMessage={() => (
          <div className="flex h-9 w-full items-center justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        )}
        styles={{
          ...stylesConfig,
        }}
        classNames={{
          ...classNamesConfig,
          control: controlProps =>
            cn(
              'rounded-none px-3 py-1 w-full !min-h-[48px] bg-transparent hover:outline-none focus:outline-none',
              'ea-multi-select-control',
              classNamesConfig?.control?.(controlProps),
            ),
          menuList: menuProps =>
            cn(
              'top-2 bg-red-500 border border-input rounded-lg py-1',
              'ea-multi-select-menu-list',
              classNamesConfig?.menuList?.(menuProps),
            ),
          placeholder: placeholderProps =>
            cn(
              'text-muted-foreground',
              'ea-multi-select-placeholder',
              classNamesConfig?.placeholder?.(placeholderProps),
            ),
          option: optionProps =>
            cn(
              'hover:bg-muted p-2',
              'ea-multi-select-option',
              classNamesConfig?.option?.(optionProps),
            ),
          noOptionsMessage: noOptionsProps =>
            cn(
              'p-2',
              'ea-multi-select-no-options-message',
              classNamesConfig?.noOptionsMessage?.(noOptionsProps),
            ),
          multiValue: multiValueProps =>
            cn(
              'bg-muted px-2 py-1 rounded-md',
              'ea-multi-select-multi-value',
              classNamesConfig?.multiValue?.(multiValueProps),
            ),
          valueContainer: valueContainerProps =>
            cn(
              'gap-2',
              'ea-multi-select-value-container',
              classNamesConfig?.valueContainer?.(valueContainerProps),
            ),
        }}
        className={cn(
          multiselectVariants({
            // Pass the CVA variant props here
            variant: props.variant,
            rounded: props.rounded,
            className: cn('ea-multi-select-container', props.className),
          }),
        )}
        // Spread all remaining react-select AsyncSelect props
        {...props}
        // Provide a similar custom container for "before" icon
        components={{
          SelectContainer: ({ children, className, innerProps }) => (
            <div
              {...innerProps}
              className={cn('flex w-full flex-row gap-0', innerProps.className, className)}
            >
              {before}
              <div className="flex-1">{children}</div>
            </div>
          ),
          ClearIndicator: ({ clearValue }) => (
            <Cross1Icon
              className={cn(
                'h-4 w-4 cursor-pointer opacity-50 hover:opacity-100',
                'ea-select__clear-icon',
              )}
              onClick={e => {
                e.stopPropagation();
                clearValue();
              }}
            />
          ),
          // If you want to customize the dropdown indicator, do so here
          ...props.components,
        }}
      />
    );
  },
);

MultiSelectAsync.displayName = 'MultiSelectAsync';
